/**
 * Client-side rendering of the current URL. In production ("path" mode) every route is a real,
 * pre-rendered HTML file and navigation is a normal page load; this re-renders the page in place on
 * load to attach behaviour. In the single-file preview ("hash" mode) it also handles `#/…` navigation.
 */
import { fillDemoData } from '../../content/demo-data';
import { REDIRECTS } from '../../config/routes';
import { SITE_CONFIG } from '../../config/site-config';
import { prefersReducedMotion, requireElement } from '../../lib/dom';
import { trackPageView } from '../analytics/analytics';
import { markActiveNavigation } from '../navigation/active-link';
import { closeDrawer } from '../navigation/drawer';
import { closeAllDropdowns } from '../navigation/dropdowns';
import { initPageBehaviors } from '../page-behaviors';
import { parseLocation } from './location';
import { applyPageMeta } from './meta';
import { cleanupPage } from './page-lifecycle';
import { NOT_FOUND_PAGE, resolvePage } from './page-registry';
import { getCurrentPath, getCurrentQuery, setCurrentLocation } from './router-state';

export function scrollToAnchor(anchor: string | null, smooth = true): boolean {
  const element = anchor ? document.getElementById(anchor) : null;
  if (!element) return false;
  element.scrollIntoView({ behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto', block: 'start' });
  return true;
}

/** Follows a legacy redirect. Returns the path to render, or null when the browser is navigating away. */
function applyRedirect(path: string, anchor: string | null): string | null {
  const target = REDIRECTS[path];
  if (!target) return path;
  if (SITE_CONFIG.ROUTING === 'path') {
    location.replace(target);
    return null;
  }
  history.replaceState(null, '', `${location.pathname}${location.search}#${target}${anchor ? `#${anchor}` : ''}`);
  return target;
}

export function renderRoute({ first = false }: { first?: boolean } = {}): void {
  const parsed = parseLocation(SITE_CONFIG.ROUTING);
  if (!parsed) return;
  const { anchor, query, queryString } = parsed;
  const path = applyRedirect(parsed.path, anchor);
  if (path === null) return;

  if (!first && path === getCurrentPath() && queryString === getCurrentQuery()) {
    scrollToAnchor(anchor);
    return;
  }

  const page = (window.RT_FORCE_404 ? null : resolvePage(path)) ?? NOT_FOUND_PAGE;
  setCurrentLocation(path, queryString);
  closeDrawer({ restoreFocus: false });
  closeAllDropdowns();
  cleanupPage();

  const main = requireElement('#main');
  main.innerHTML = fillDemoData(page.render(query));
  applyPageMeta(page, path + (queryString ? `?${queryString}` : ''));
  document.documentElement.lang = page.meta.lang ?? 'en';
  markActiveNavigation(path);
  initPageBehaviors(main);
  page.init?.(main);

  if (anchor) {
    requestAnimationFrame(() => {
      if (!scrollToAnchor(anchor, false)) window.scrollTo(0, 0);
    });
  } else {
    window.scrollTo(0, 0);
  }

  if (!first) {
    const heading = main.querySelector('h1');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
    requireElement('#route-status').textContent = page.meta.title.split(' | ')[0];
    trackPageView();
  }
}

/** Renders the current route again from scratch, keeping the scroll position (e.g. after a theme change). */
export function rerenderRoute(): void {
  const scrollY = window.scrollY;
  setCurrentLocation(null);
  renderRoute({ first: true });
  window.scrollTo(0, scrollY);
}
